import { Colors, Icon, Label, Spinner, Text, TextInput } from '@amelio/ui-library';
import { useId, useState } from 'react';

import {
    CAREER_STEP_TITLE_MAX_LENGTH,
    toCareerStepRequest,
    toRefusalMessage,
} from './careerStepValidation';
import { GROWTH_CAPTION } from './growthStyles';
import { HorizonYearInput } from './HorizonYearInput';
import { readTargetStep } from './targetPosition';

import type { RosterEmployee } from '@/api/competency-services/profile-assignment/profileAssignmentService';
import type { ProfileSelectorResult } from '@/components/CompetencyProfileAssignment';
import type { CareerStep, TargetPosition } from '@/types/career-growth';
import type { EmployeeCompetencyAssignment } from '@/types/performance-profile';

import { isValidProfileId } from '@/api/competency-services/profile-assignment/profileAssignmentService';
import {
    useCreateCareerStep,
    useGetEmployeeCompetencyAssignment,
    useGetEmployeeGrowth,
    useUpdateCareerStep,
} from '@/api/performance-profile';
import {
    fullName,
    PROFILE_SELECTOR_SURFACE,
    ProfileSelector,
} from '@/components/CompetencyProfileAssignment';
import { useAmelioTranslations } from '@/hooks';
import { useReportedWrite } from '@/hooks/utils/useReportedWrite';

const NO_OVERLAY = { addedCompetencyIds: [], removedCompetencyIds: [] };

interface TargetProfileAssignmentProps {
    employee: RosterEmployee;
    onDone: () => void;
    onCancel: () => void;
}

/**
 * The competencies the employee holds today: their profile's and the ones added for them, less the
 * ones removed for them.
 */
const readHeldCompetencyIds = (
    assignment: EmployeeCompetencyAssignment | undefined,
): ReadonlySet<string> =>
    new Set(
        [
            ...(assignment?.profileCompetencyGroups ?? []),
            ...(assignment?.additionalCompetencyGroups ?? []),
        ]
            .flatMap((group) => group.competencies)
            .filter((competency) => !competency.isRemoved)
            .map((competency) => competency.id),
    );

/**
 * "Éditer le profil" of the position the employee aims for: its name and its horizon, then the
 * competency profile it requires, with the competencies added or removed for the employee. All of
 * it lives on the employee's `Target` career step, which the save writes -- updated when there is
 * one, created otherwise.
 */
export const TargetProfileAssignment = ({ employee, ...props }: TargetProfileAssignmentProps) => {
    const { t } = useAmelioTranslations();
    const { data: growth, isError } = useGetEmployeeGrowth(employee.id);
    // Already read by the profile page. Without it, nothing is greyed: the marks are a help to the
    // reader, and a failed read must not hold the screen back.
    const { data: assignment, isPending: isAssignmentPending } = useGetEmployeeCompetencyAssignment(
        employee.id,
    );

    if (isError) {
        return <p className={'text-error p-8'}>{t('performanceProfile.error')}</p>;
    }

    if (!growth || isAssignmentPending) {
        return (
            <div
                className={'flex items-center justify-center p-8'}
                data-testid={'target-profile-loading'}
            >
                <Spinner
                    size={'md'}
                    color={Colors.PRIMARY}
                />
            </div>
        );
    }

    return (
        <TargetProfileEditor
            employee={employee}
            targetStep={readTargetStep(growth)}
            targetPosition={growth.targetPosition}
            heldCompetencyIds={readHeldCompetencyIds(assignment)}
            {...props}
        />
    );
};

interface TargetProfileEditorProps extends TargetProfileAssignmentProps {
    targetStep: CareerStep | null;
    /** What the growth tab shows as the target, which the screen starts from. */
    targetPosition: TargetPosition | null;
    heldCompetencyIds: ReadonlySet<string>;
}

const TargetProfileEditor = ({
    employee,
    targetStep,
    targetPosition,
    heldCompetencyIds,
    onDone,
    onCancel,
}: TargetProfileEditorProps) => {
    const { t } = useAmelioTranslations();
    const titleId = useId();
    const runWrite = useReportedWrite();
    const { mutateAsync: createStep, isPending: isCreating } = useCreateCareerStep(employee.id);
    const { mutateAsync: updateStep, isPending: isUpdating } = useUpdateCareerStep(employee.id);

    // The tile may show the latest aspiration rather than the step: the screen opens on what the
    // reader just saw, and saving makes the step the most recent record, so the tile then shows it.
    const [title, setTitle] = useState(targetPosition?.title ?? '');
    // An aspiration carries no horizon: the step's own is the only one to start from.
    const [horizon, setHorizon] = useState(targetStep?.startDate ?? null);
    const initialProfileId = targetPosition?.competencyProfileId;
    // The step's adjustments belong to the step's profile: a more recent aspiration may name
    // another one, which the screen then opens on as it stands.
    const overlay =
        targetStep && targetStep.competencyProfileId === initialProfileId ? targetStep : NO_OVERLAY;
    const horizonLabel = t('performanceProfile.growth.stepsEditor.horizonOptional');

    // The screen saves only once a profile is chosen, and a target named by its profile needs no
    // title: the name is the reader's to give or not.
    const handleConfirm = async ({
        profileId,
        addedCompetencyIds,
        removedCompetencyIds,
    }: ProfileSelectorResult) => {
        const request = {
            ...toCareerStepRequest({
                title,
                kind: 'Target',
                startDate: horizon,
                endDate: null,
                competencyProfileId: profileId,
            }),
            addedCompetencyIds,
            removedCompetencyIds,
        };
        const isSaved = await runWrite({
            write: () =>
                targetStep ? updateStep({ stepId: targetStep.id, request }) : createStep(request),
            success: 'performanceProfile.growth.targetProfile.saved',
            failure: 'performanceProfile.growth.stepsEditor.saveError',
            explainRefusal: toRefusalMessage,
        });
        if (isSaved) {
            onDone();
        }
    };

    return (
        <ProfileSelector
            employee={employee}
            profileFor={'target'}
            overlay={overlay}
            heldCompetencyIds={heldCompetencyIds}
            initialProfileId={isValidProfileId(initialProfileId) ? initialProfileId : undefined}
            isSubmitting={isCreating || isUpdating}
            onConfirm={(result) => void handleConfirm(result)}
            onCancel={onCancel}
        >
            <div className={`${PROFILE_SELECTOR_SURFACE} flex flex-col gap-6 p-4 md:p-6`}>
                <div className={'flex flex-col gap-2'}>
                    <Label
                        htmlFor={titleId}
                        color={Colors.NAVY}
                    >
                        {t('performanceProfile.growth.targetProfile.titleOptional')}
                    </Label>
                    <span className={GROWTH_CAPTION}>
                        {t('performanceProfile.growth.targetProfile.titleHelp', {
                            name: fullName(employee),
                        })}
                    </span>
                    <div className={'max-w-md'}>
                        <TextInput
                            id={titleId}
                            value={title}
                            onChange={setTitle}
                            placeholder={t(
                                'performanceProfile.growth.targetProfile.titlePlaceholder',
                            )}
                            maxLength={CAREER_STEP_TITLE_MAX_LENGTH}
                            variant={'white'}
                            bordered
                            dataTestId={'target-profile-title'}
                        />
                    </div>
                </div>

                <div className={'flex max-w-xs flex-col gap-2'}>
                    <Text
                        size={'small'}
                        color={Colors.NAVY}
                        weight={'medium'}
                    >
                        {horizonLabel}
                    </Text>
                    <HorizonYearInput
                        value={horizon}
                        onChange={setHorizon}
                        dataTestId={'target-profile-horizon'}
                    />
                </div>

                {/* The API serves the target step to the employee's managers and HR only; the
                    employee's own portal reads the target profile, never its name or horizon. */}
                <p className={`${GROWTH_CAPTION} flex items-center gap-2`}>
                    <Icon
                        name={'security-lock'}
                        size={'sm'}
                        ariaHidden
                    />
                    {t('performanceProfile.growth.targetProfile.internalNote', {
                        name: fullName(employee),
                    })}
                </p>
            </div>
        </ProfileSelector>
    );
};
